/**
 * #1443 — one predicate for "this hotel's contact profile is incomplete",
 * shared by the warning banner and any page that needs the same answer.
 *
 * #1321 is the reason this lives in a shared helper rather than inside the
 * banner: when the affiliate banner owned its own rules it warned about details
 * the page it linked to was already displaying. A banner and its target must
 * never be able to disagree.
 *
 * A contact counts as populated when it carries a name and an email. Phone is
 * genuinely optional — hotels frequently route through a shared mailbox.
 *
 * Naming note: HotelDetailsPage shows these as "Affiliation Contact" and
 * "Finance Contact", but stores them in hotels.contact_info as
 * `commercialContact` and `technicalContact`. The display-to-storage mapping is
 * contained here so no caller has to know it.
 *
 * #1673: partner-service utils/hotelCompletion.ts applies this same rule
 * server-side for the admin Hotels list Completion %. Change both together.
 */
export interface HotelContact {
  name?: string | null
  email?: string | null
  phone?: string | null
}

export interface HotelContactSource {
  contactInfo?: {
    commercialContact?: HotelContact | null
    technicalContact?: HotelContact | null
  } | null
}

export type MissingHotelContact = 'affiliationContact' | 'financeContact'

const isPopulated = (c: HotelContact | null | undefined): boolean =>
  !!c && !!(c.name ?? '').trim() && !!(c.email ?? '').trim()

export function evaluateHotelContactCompleteness(
  source: HotelContactSource,
): { isIncomplete: boolean; missing: MissingHotelContact[] } {
  const info = source.contactInfo ?? null
  const missing: MissingHotelContact[] = []
  if (!isPopulated(info?.commercialContact)) missing.push('affiliationContact')
  if (!isPopulated(info?.technicalContact)) missing.push('financeContact')
  return { isIncomplete: missing.length > 0, missing }
}
